import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as assetSaleAPI from '../../api/PurchaseAPI/assetSaleAPI';

// Async Thunks
// =============================================

export const fetchAssetItems = createAsyncThunk(
    'assetSale/fetchAssetItems',
    async (params, { rejectWithValue }) => {
        try {
            const response = await assetSaleAPI.getAssetItems(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Asset Items');
        }
    }
);

export const addAssetSaleItem = createAsyncThunk(
    'assetSale/addAssetSaleItem',
    async (params, { rejectWithValue }) => {
        try {
            const response = await assetSaleAPI.saveAssetSaleItems(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to add item');
        }
    }
);

export const fetchAssetSaleItemDetails = createAsyncThunk(
    'assetSale/fetchAssetSaleItemDetails',
    async (_, { rejectWithValue }) => {
        try {
            const response = await assetSaleAPI.getAssetSaleItemDetails();
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch staged items');
        }
    }
);

export const fetchAssetAmount = createAsyncThunk(
    'assetSale/fetchAssetAmount',
    async (_, { rejectWithValue }) => {
        try {
            const response = await assetSaleAPI.getAssetAmount();
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Asset Amount');
        }
    }
);

export const deleteAssetSaleItem = createAsyncThunk(
    'assetSale/deleteAssetSaleItem',
    async (iId, { rejectWithValue }) => {
        try {
            const response = await assetSaleAPI.deleteAssetSaleItem(iId);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to delete item');
        }
    }
);

export const saveAssetSaleData = createAsyncThunk(
    'assetSale/saveAssetSaleData',
    async (params, { rejectWithValue }) => {
        try {
            const response = await assetSaleAPI.saveAssetSaleData(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to save Asset Sale');
        }
    }
);

// Initial State
// =============
const initialState = {
    assetItems: [],
    stagedItems: [],
    assetValue: 0,
    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'
    addItemStatus: null, // null | 'pending' | 'success' | 'failed'
    deleteStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        assetItems: false,
        stagedItems: false,
        assetValue: false,
        addItem: false,
        deleteItem: false,
        save: false,
    },

    errors: {
        assetItems: null,
        stagedItems: null,
        assetValue: null,
        addItem: null,
        deleteItem: null,
        save: null,
    },
};

const assetSaleSlice = createSlice({
    name: 'assetSale',
    initialState,
    reducers: {
        clearSaveResult: (state) => {
            state.saveResult = null;
            state.saveStatus = null;
            state.errors.save = null;
        },
        clearAddItemStatus: (state) => {
            state.addItemStatus = null;
            state.errors.addItem = null;
        },
        clearDeleteStatus: (state) => {
            state.deleteStatus = null;
            state.errors.deleteItem = null;
        },
        resetAssetSale: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAssetItems.pending, (state) => {
                state.loading.assetItems = true;
                state.errors.assetItems = null;
            })
            .addCase(fetchAssetItems.fulfilled, (state, action) => {
                state.loading.assetItems = false;
                state.assetItems = action.payload?.Data || [];
            })
            .addCase(fetchAssetItems.rejected, (state, action) => {
                state.loading.assetItems = false;
                state.errors.assetItems = action.payload;
                state.assetItems = [];
            });

        builder
            .addCase(addAssetSaleItem.pending, (state) => {
                state.loading.addItem = true;
                state.errors.addItem = null;
                state.addItemStatus = 'pending';
            })
            .addCase(addAssetSaleItem.fulfilled, (state, action) => {
                state.loading.addItem = false;
                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                if (responseStr === 'Submited') {
                    state.addItemStatus = 'success';
                } else {
                    state.addItemStatus = 'failed';
                    state.errors.addItem = responseStr || 'Failed to add item';
                }
            })
            .addCase(addAssetSaleItem.rejected, (state, action) => {
                state.loading.addItem = false;
                state.errors.addItem = action.payload;
                state.addItemStatus = 'failed';
            });

        builder
            .addCase(fetchAssetSaleItemDetails.pending, (state) => {
                state.loading.stagedItems = true;
                state.errors.stagedItems = null;
            })
            .addCase(fetchAssetSaleItemDetails.fulfilled, (state, action) => {
                state.loading.stagedItems = false;
                state.stagedItems = action.payload?.Data || [];
            })
            .addCase(fetchAssetSaleItemDetails.rejected, (state, action) => {
                state.loading.stagedItems = false;
                state.errors.stagedItems = action.payload;
                state.stagedItems = [];
            });

        builder
            .addCase(fetchAssetAmount.pending, (state) => {
                state.loading.assetValue = true;
                state.errors.assetValue = null;
            })
            .addCase(fetchAssetAmount.fulfilled, (state, action) => {
                state.loading.assetValue = false;
                const data = action.payload?.Data || [];
                state.assetValue = data.length > 0 ? (data[data.length - 1].AssetValue || 0) : 0;
            })
            .addCase(fetchAssetAmount.rejected, (state, action) => {
                state.loading.assetValue = false;
                state.errors.assetValue = action.payload;
                state.assetValue = 0;
            });

        builder
            .addCase(deleteAssetSaleItem.pending, (state) => {
                state.loading.deleteItem = true;
                state.errors.deleteItem = null;
                state.deleteStatus = 'pending';
            })
            .addCase(deleteAssetSaleItem.fulfilled, (state, action) => {
                state.loading.deleteItem = false;
                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                if (responseStr === 'Submited') {
                    state.deleteStatus = 'success';
                } else {
                    state.deleteStatus = 'failed';
                    state.errors.deleteItem = responseStr || 'Failed to delete item';
                }
            })
            .addCase(deleteAssetSaleItem.rejected, (state, action) => {
                state.loading.deleteItem = false;
                state.errors.deleteItem = action.payload;
                state.deleteStatus = 'failed';
            });

        builder
            .addCase(saveAssetSaleData.pending, (state) => {
                state.loading.save = true;
                state.errors.save = null;
                state.saveStatus = 'pending';
            })
            .addCase(saveAssetSaleData.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;

                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                const [status] = responseStr.split(',');

                if (status === 'Submited') {
                    state.saveStatus = 'success';
                } else {
                    state.saveStatus = 'failed';
                    state.errors.save = responseStr || 'Save failed';
                }
            })
            .addCase(saveAssetSaleData.rejected, (state, action) => {
                state.loading.save = false;
                state.errors.save = action.payload;
                state.saveStatus = 'failed';
                state.saveResult = null;
            });
    },
});

export const {
    clearSaveResult,
    clearAddItemStatus,
    clearDeleteStatus,
    resetAssetSale,
} = assetSaleSlice.actions;

// Selectors
// =========
export const selectAssetItemsArray = (state) => {
    const items = state.assetSale.assetItems;
    return Array.isArray(items) ? items : [];
};
export const selectAssetItemsLoading = (state) => state.assetSale.loading.assetItems;

export const selectStagedItemsArray = (state) => {
    const items = state.assetSale.stagedItems;
    return Array.isArray(items) ? items : [];
};
export const selectStagedItemsLoading = (state) => state.assetSale.loading.stagedItems;

export const selectAssetValue = (state) => state.assetSale.assetValue;
export const selectAssetValueLoading = (state) => state.assetSale.loading.assetValue;

export const selectAddItemStatus = (state) => state.assetSale.addItemStatus;
export const selectAddItemLoading = (state) => state.assetSale.loading.addItem;
export const selectAddItemError = (state) => state.assetSale.errors.addItem;

export const selectDeleteStatus = (state) => state.assetSale.deleteStatus;
export const selectDeleteLoading = (state) => state.assetSale.loading.deleteItem;
export const selectDeleteError = (state) => state.assetSale.errors.deleteItem;

export const selectSaveStatus = (state) => state.assetSale.saveStatus;
export const selectSaveLoading = (state) => state.assetSale.loading.save;
export const selectSaveError = (state) => state.assetSale.errors.save;

export default assetSaleSlice.reducer;
